import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { bankingService } from '../../../services/bankingService';


const ManualTransactionForm = ({ isOpen, onClose, onSubmit, bankAccounts = [] }) => {
  const [formData, setFormData] = useState({
    account: '',
    date: new Date()?.toISOString()?.split('T')?.[0],
    description: '',
    amount: '',
    category: '',
    transactionType: 'expense',
    reference: '',
    memo: '',
    payee: '',
    customerName: '',
    selectedCustomer: '',
    selectedInvoice: '',
    vendorName: '',
    selectedVendor: '',
    selectedBill: '',
    transferToBankName: '',
    transferToBankAccount: ''
  });

  const [customers, setCustomers] = useState([]);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorBills, setVendorBills] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingBills, setLoadingBills] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const [errors, setErrors] = useState({});

  // Load data when component opens
  useEffect(() => {
    if (isOpen) {
      loadCustomers();
      loadVendors();
      loadEnhancedCategories();
    }
  }, [isOpen]);

  // Load invoices when customer is selected
  useEffect(() => {
    if (formData?.selectedCustomer && (formData?.category === 'customer_payment' || formData?.category === 'customer_advance')) {
      loadCustomerInvoices(formData?.selectedCustomer);
    } else {
      setCustomerInvoices([]);
      setFormData(prev => ({ ...prev, selectedInvoice: '' }));
    }
  }, [formData?.selectedCustomer, formData?.category]);

  // Load bills when vendor is selected
  useEffect(() => {
    if (formData?.selectedVendor && (formData?.category === 'vendor_payment' || formData?.category === 'vendor_advance')) {
      loadVendorBills(formData?.selectedVendor);
    } else {
      setVendorBills([]);
      setFormData(prev => ({ ...prev, selectedBill: '' }));
    }
  }, [formData?.selectedVendor, formData?.category]);

  const loadCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const customerData = await bankingService?.getAllCustomers();
      setCustomers(customerData || []);
    } catch (error) {
      console.error('Error loading customers:', error);
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadVendors = async () => {
    try {
      setLoadingVendors(true);
      const vendorData = await bankingService?.getAllVendors();
      setVendors(vendorData || []);
    } catch (error) {
      console.error('Error loading vendors:', error);
      setVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  };

  const loadEnhancedCategories = async () => {
    try {
      setLoadingCategories(true);
      const enhancedOptions = await bankingService?.getEnhancedCategoryOptions();
      setCategoryOptions(enhancedOptions || []);
    } catch (error) {
      console.error('Error loading enhanced categories:', error);
      // Fallback to basic categories
      setCategoryOptions([
        { value: '', label: 'Select Category', isHeader: false },
        { value: 'customer_payment', label: 'Customer Payment', isHeader: false },
        { value: 'customer_advance', label: 'Customer Advance', isHeader: false },
        { value: 'vendor_payment', label: 'Vendor Payment', isHeader: false },
        { value: 'vendor_advance', label: 'Vendor Advance', isHeader: false },
        { value: 'transfer_to_bank', label: 'Transfer to Another Bank', isHeader: false },
        { value: 'Revenue', label: 'Revenue', isHeader: false },
        { value: 'Office Supplies', label: 'Office Supplies', isHeader: false },
        { value: 'Other', label: 'Other', isHeader: false }
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadCustomerInvoices = async (customerId) => {
    try {
      setLoadingInvoices(true);
      const invoiceData = await bankingService?.getCustomerInvoices(customerId);
      setCustomerInvoices(invoiceData || []);
    } catch (error) {
      console.error('Error loading customer invoices:', error);
      setCustomerInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const loadVendorBills = async (vendorId) => {
    try {
      setLoadingBills(true);
      const billData = await bankingService?.getVendorBills(vendorId);
      setVendorBills(billData || []);
    } catch (error) {
      console.error('Error loading vendor bills:', error);
      setVendorBills([]);
    } finally {
      setLoadingBills(false);
    }
  };

  // Auto-fill invoice data when invoice is selected
  const handleInvoiceSelection = async (invoiceId) => {
    if (!invoiceId) {
      setFormData(prev => ({
        ...prev,
        selectedInvoice: '',
        amount: '',
        description: '',
        reference: ''
      }));
      return;
    }

    try {
      const invoiceDetails = await bankingService?.getInvoiceDetails(invoiceId);
      if (invoiceDetails) {
        // Auto-fill form with invoice data
        setFormData(prev => ({
          ...prev,
          selectedInvoice: invoiceId,
          amount: (invoiceDetails?.balance_due || invoiceDetails?.total_amount || 0)?.toString(),
          description: `Payment for ${invoiceDetails?.invoice_number}`,
          reference: invoiceDetails?.invoice_number
        }));
      }
    } catch (error) {
      console.error('Error loading invoice details:', error);
    }
  };

  // Auto-fill bill data when bill is selected
  const handleBillSelection = async (billId) => {
    if (!billId) {
      setFormData(prev => ({
        ...prev,
        selectedBill: '',
        amount: '',
        description: '',
        reference: ''
      }));
      return;
    }

    try {
      const billDetails = await bankingService?.getBillDetails(billId);
      if (billDetails) {
        // Auto-fill form with bill data
        setFormData(prev => ({
          ...prev,
          selectedBill: billId,
          amount: (billDetails?.balance_due || billDetails?.total_amount || 0)?.toString(),
          description: `Payment for ${billDetails?.bill_number}`,
          reference: billDetails?.bill_number
        }));
      }
    } catch (error) {
      console.error('Error loading bill details:', error);
    }
  };

  // Get the display label for selected category
  const getSelectedCategoryLabel = () => {
    if (!formData?.category) return 'Select Category';
    
    const selectedOption = categoryOptions?.find(option => option?.value === formData?.category);
    if (selectedOption) {
      return selectedOption?.label?.replace(/^\s+/, '')?.replace(/^──\s*/, '')?.replace(/\s*──$/, '');
    }
    
    return formData?.category?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
  };

  const accountOptions = [
    { value: '', label: 'Select Account' },
    ...bankAccounts?.map(account => ({
      value: account?.id,
      label: `${account?.bankName} (...${account?.accountNumber?.slice(-4)})`
    }))
  ];

  const customerOptions = [
    { value: '', label: 'Select Customer' },
    ...customers?.map(customer => ({
      value: customer?.id,
      label: `${customer?.customer_name} (${customer?.customer_number})`
    }))
  ];

  const vendorOptions = [
    { value: '', label: 'Select Vendor' },
    ...vendors?.map(vendor => ({
      value: vendor?.id,
      label: `${vendor?.vendor_name} (${vendor?.vendor_number})`
    }))
  ];

  const billOptions = [
    { value: '', label: 'Select Bill' },
    ...vendorBills?.map(bill => ({
      value: bill?.id,
      label: `${bill?.bill_number} - ${(bill?.balance_due || bill?.total_amount || 0)?.toFixed(2)} (${bill?.status})`
    }))
  ];

  const invoiceOptions = [
    { value: '', label: 'Select Invoice' },
    ...customerInvoices?.map(invoice => ({
      value: invoice?.id,
      label: `${invoice?.invoice_number} - ${(invoice?.balance_due || invoice?.total_amount || 0)?.toFixed(2)} (${invoice?.status})`
    }))
  ];

  const transactionTypeOptions = [
    { value: 'expense', label: 'Expense' },
    { value: 'income', label: 'Income' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Auto-set transaction type based on category
    if (field === 'category') {
      if (value === 'customer_payment' || value === 'customer_advance') {
        setFormData(prev => ({ ...prev, transactionType: 'income' }));
      } else if (value === 'vendor_payment' || value === 'vendor_advance' || value === 'transfer_to_bank') {
        setFormData(prev => ({ ...prev, transactionType: 'expense' }));
      } else if (value?.startsWith('account_')) {
        // For account-based categories, determine type based on account type
        const selectedOption = categoryOptions?.find(option => option?.value === value);
        if (selectedOption?.accountType) {
          // Asset accounts typically receive money (income), liability/expense accounts typically spend money (expense)
          if (selectedOption?.accountType === 'asset' || selectedOption?.accountType === 'revenue') {
            setFormData(prev => ({ ...prev, transactionType: 'income' }));
          } else if (selectedOption?.accountType === 'expense' || selectedOption?.accountType === 'liability') {
            setFormData(prev => ({ ...prev, transactionType: 'expense' }));
          }
        }
      }
      
      // Clear customer/invoice selection when category changes
      if (value !== 'customer_payment' && value !== 'customer_advance') {
        setFormData(prev => ({ 
          ...prev, 
          selectedCustomer: '', 
          selectedInvoice: '',
          customerName: ''
        }));
        setCustomerInvoices([]);
      }

      // Clear vendor/bill selection when category changes
      if (value !== 'vendor_payment' && value !== 'vendor_advance') {
        setFormData(prev => ({ 
          ...prev, 
          selectedVendor: '', 
          selectedBill: '',
          vendorName: ''
        }));
        setVendorBills([]);
      }
    }

    // Handle customer selection
    if (field === 'selectedCustomer') {
      const selectedCustomer = customers?.find(c => c?.id === value);
      setFormData(prev => ({
        ...prev,
        customerName: selectedCustomer?.customer_name || '',
        selectedInvoice: ''
      }));
    }

    // Handle vendor selection
    if (field === 'selectedVendor') {
      const selectedVendor = vendors?.find(v => v?.id === value);
      setFormData(prev => ({
        ...prev,
        vendorName: selectedVendor?.vendor_name || '',
        selectedBill: ''
      }));
    }

    // Handle invoice selection with autofill
    if (field === 'selectedInvoice') {
      handleInvoiceSelection(value);
    }

    // Handle bill selection with autofill
    if (field === 'selectedBill') {
      handleBillSelection(value);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.account) newErrors.account = 'Account is required';
    if (!formData?.date) newErrors.date = 'Date is required';
    if (!formData?.description) newErrors.description = 'Description is required';
    if (!formData?.amount) newErrors.amount = 'Amount is required';
    if (!formData?.category) newErrors.category = 'Category is required';
    
    if (formData?.amount && isNaN(parseFloat(formData?.amount))) {
      newErrors.amount = 'Amount must be a valid number';
    }

    // Validate additional fields for specific categories
    if (formData?.category === 'customer_payment' || formData?.category === 'customer_advance') {
      if (!formData?.selectedCustomer) newErrors.selectedCustomer = 'Customer selection is required';
    }

    if (formData?.category === 'vendor_payment' || formData?.category === 'vendor_advance') {
      if (!formData?.selectedVendor) newErrors.selectedVendor = 'Vendor selection is required';
    }

    if (formData?.category === 'transfer_to_bank') {
      if (!formData?.transferToBankName) newErrors.transferToBankName = 'Destination bank name is required';
      if (!formData?.transferToBankAccount) newErrors.transferToBankAccount = 'Destination account number is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    // Build description based on category
    let enhancedDescription = formData?.description;
    let finalCategory = formData?.category;
    
    if (formData?.category === 'customer_payment') {
      enhancedDescription = `Customer Payment from ${formData?.customerName} - ${formData?.description}`;
    } else if (formData?.category === 'customer_advance') {
      enhancedDescription = `Customer Advance from ${formData?.customerName} - ${formData?.description}`;
    } else if (formData?.category === 'vendor_payment') {
      enhancedDescription = `Vendor Payment to ${formData?.vendorName} - ${formData?.description}`;
    } else if (formData?.category === 'vendor_advance') {
      enhancedDescription = `Vendor Advance to ${formData?.vendorName} - ${formData?.description}`;
    } else if (formData?.category === 'transfer_to_bank') {
      enhancedDescription = `Transfer to ${formData?.transferToBankName} (Account: ${formData?.transferToBankAccount}) - ${formData?.description}`;
    } else if (formData?.category?.startsWith('account_')) {
      // For account-based categories, use the account name as category
      const selectedOption = categoryOptions?.find(option => option?.value === formData?.category);
      if (selectedOption?.accountName) {
        finalCategory = selectedOption?.accountName;
        enhancedDescription = `${selectedOption?.accountName} - ${formData?.description}`;
      }
    }
    
    const transactionData = {
      bankAccountId: formData?.account,
      transactionDate: formData?.date,
      description: enhancedDescription,
      amount: formData?.transactionType === 'expense' 
        ? -Math.abs(parseFloat(formData?.amount)) 
        : Math.abs(parseFloat(formData?.amount)),
      category: finalCategory,
      referenceNumber: formData?.reference,
      reconciliationStatus: 'unreconciled'
    };
    
    onSubmit?.(transactionData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      account: '',
      date: new Date()?.toISOString()?.split('T')?.[0],
      description: '',
      amount: '',
      category: '',
      transactionType: 'expense',
      reference: '',
      memo: '',
      payee: '',
      customerName: '',
      selectedCustomer: '',
      selectedInvoice: '',
      vendorName: '',
      selectedVendor: '',
      selectedBill: '',
      transferToBankName: '',
      transferToBankAccount: ''
    });
    setErrors({});
    setCustomers([]);
    setCustomerInvoices([]);
    setVendors([]);
    setVendorBills([]);
    setCategoryOptions([]);
    onClose?.();
  };

  // Check if we should show additional fields based on selected category
  const showCustomerFields = formData?.category === 'customer_payment' || formData?.category === 'customer_advance';
  const showVendorFields = formData?.category === 'vendor_payment' || formData?.category === 'vendor_advance';
  const showTransferFields = formData?.category === 'transfer_to_bank';
  const isAccountBasedCategory = formData?.category?.startsWith('account_');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Add Manual Transaction</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Record a transaction that doesn't appear in your bank feed
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            iconName="X"
          />
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Transaction Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Account <span className="text-error">*</span>
                </label>
                <Select
                  options={accountOptions}
                  value={formData?.account}
                  onChange={(value) => handleChange('account', value)}
                  placeholder="Select account"
                  error={errors?.account}
                />
                {errors?.account && (
                  <p className="text-error text-sm mt-1">{errors?.account}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Date <span className="text-error">*</span>
                </label>
                <Input
                  type="date"
                  value={formData?.date}
                  onChange={(e) => handleChange('date', e?.target?.value)}
                  error={errors?.date}
                />
                {errors?.date && (
                  <p className="text-error text-sm mt-1">{errors?.date}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Category <span className="text-error">*</span>
                </label>
                <div className="relative">
                  <Select
                    options={categoryOptions}
                    value={formData?.category}
                    onChange={(value) => handleChange('category', value)}
                    placeholder={loadingCategories ? "Loading categories..." : "Select category"}
                    error={errors?.category}
                    disabled={loadingCategories}
                    className="account-type-select"
                  />
                  {loadingCategories && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    </div>
                  )}
                </div>
                {errors?.category && (
                  <p className="text-error text-sm mt-1">{errors?.category}</p>
                )}
                {formData?.category && isAccountBasedCategory && (
                  <p className="text-xs text-success mt-1">
                    ✓ Account-based category selected: {getSelectedCategoryLabel()}
                  </p>
                )}
                {loadingCategories && (
                  <p className="text-xs text-muted-foreground mt-1">Loading account types and categories...</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Type <span className="text-error">*</span>
                </label>
                <Select
                  options={transactionTypeOptions}
                  value={formData?.transactionType}
                  onChange={(value) => handleChange('transactionType', value)}
                  disabled={showCustomerFields || showVendorFields || showTransferFields || isAccountBasedCategory}
                />
                {(showCustomerFields || showVendorFields || showTransferFields || isAccountBasedCategory) && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Type is automatically set based on selected category
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Amount <span className="text-error">*</span>
                </label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData?.amount}
                  onChange={(e) => handleChange('amount', e?.target?.value)}
                  error={errors?.amount}
                />
                {errors?.amount && (
                  <p className="text-error text-sm mt-1">{errors?.amount}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Enter the amount without +/- sign. Type will determine the direction.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description <span className="text-error">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Enter transaction description"
                  value={formData?.description}
                  onChange={(e) => handleChange('description', e?.target?.value)}
                  error={errors?.description}
                />
                {errors?.description && (
                  <p className="text-error text-sm mt-1">{errors?.description}</p>
                )}
              </div>
            </div>
          </div>

          {/* Vendor-specific fields */}
          {showVendorFields && (
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Vendor Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Select Vendor <span className="text-error">*</span>
                  </label>
                  <Select
                    options={vendorOptions}
                    value={formData?.selectedVendor}
                    onChange={(value) => handleChange('selectedVendor', value)}
                    placeholder={loadingVendors ? "Loading vendors..." : "Choose vendor"}
                    disabled={loadingVendors}
                    error={errors?.selectedVendor}
                  />
                  {errors?.selectedVendor && (
                    <p className="text-error text-sm mt-1">{errors?.selectedVendor}</p>
                  )}
                  {loadingVendors && (
                    <p className="text-xs text-muted-foreground mt-1">Loading vendors...</p>
                  )}
                </div>

                {formData?.selectedVendor && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Related Bill (Optional)
                    </label>
                    <Select
                      options={billOptions}
                      value={formData?.selectedBill}
                      onChange={(value) => handleChange('selectedBill', value)}
                      placeholder={loadingBills ? "Loading bills..." : "Select bill to autofill"}
                      disabled={loadingBills}
                    />
                    {loadingBills && (
                      <p className="text-xs text-muted-foreground mt-1">Loading vendor bills...</p>
                    )}
                    {!loadingBills && vendorBills?.length === 0 && formData?.selectedVendor && (
                      <p className="text-xs text-muted-foreground mt-1">No unpaid bills found for this vendor</p>
                    )}
                    {formData?.selectedBill && (
                      <p className="text-xs text-success mt-1">✓ Bill data auto-filled below</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Reference</label>
                  <Input
                    type="text"
                    placeholder="Bill number, receipt, etc."
                    value={formData?.reference}
                    onChange={(e) => handleChange('reference', e?.target?.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Customer-specific fields */}
          {showCustomerFields && (
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Customer Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Select Customer <span className="text-error">*</span>
                  </label>
                  <Select
                    options={customerOptions}
                    value={formData?.selectedCustomer}
                    onChange={(value) => handleChange('selectedCustomer', value)}
                    placeholder={loadingCustomers ? "Loading customers..." : "Choose customer"}
                    disabled={loadingCustomers}
                    error={errors?.selectedCustomer}
                  />
                  {errors?.selectedCustomer && (
                    <p className="text-error text-sm mt-1">{errors?.selectedCustomer}</p>
                  )}
                  {loadingCustomers && (
                    <p className="text-xs text-muted-foreground mt-1">Loading customers...</p>
                  )}
                </div>

                {formData?.selectedCustomer && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Related Invoice (Optional)
                    </label>
                    <Select
                      options={invoiceOptions}
                      value={formData?.selectedInvoice}
                      onChange={(value) => handleChange('selectedInvoice', value)}
                      placeholder={loadingInvoices ? "Loading invoices..." : "Select invoice to autofill"}
                      disabled={loadingInvoices}
                    />
                    {loadingInvoices && (
                      <p className="text-xs text-muted-foreground mt-1">Loading customer invoices...</p>
                    )}
                    {!loadingInvoices && customerInvoices?.length === 0 && formData?.selectedCustomer && (
                      <p className="text-xs text-muted-foreground mt-1">No unpaid invoices found for this customer</p>
                    )}
                    {formData?.selectedInvoice && (
                      <p className="text-xs text-success mt-1">✓ Invoice data auto-filled below</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Reference</label>
                  <Input
                    type="text"
                    placeholder="Invoice number, receipt, etc."
                    value={formData?.reference}
                    onChange={(e) => handleChange('reference', e?.target?.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Transfer-specific fields */}
          {showTransferFields && (
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Transfer Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Destination Bank Name <span className="text-error">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter destination bank name"
                    value={formData?.transferToBankName}
                    onChange={(e) => handleChange('transferToBankName', e?.target?.value)}
                    error={errors?.transferToBankName}
                  />
                  {errors?.transferToBankName && (
                    <p className="text-error text-sm mt-1">{errors?.transferToBankName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Destination Account Number <span className="text-error">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter destination account number"
                    value={formData?.transferToBankAccount}
                    onChange={(e) => handleChange('transferToBankAccount', e?.target?.value)}
                    error={errors?.transferToBankAccount}
                  />
                  {errors?.transferToBankAccount && (
                    <p className="text-error text-sm mt-1">{errors?.transferToBankAccount}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Transfer Reference</label>
                  <Input
                    type="text"
                    placeholder="Wire transfer reference, etc."
                    value={formData?.reference}
                    onChange={(e) => handleChange('reference', e?.target?.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* General fields for all transaction types */}
          {!showCustomerFields && !showVendorFields && !showTransferFields && (
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Additional Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Payee</label>
                  <Input
                    type="text"
                    placeholder="Enter payee name"
                    value={formData?.payee}
                    onChange={(e) => handleChange('payee', e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Reference</label>
                  <Input
                    type="text"
                    placeholder="Check number, invoice number, etc."
                    value={formData?.reference}
                    onChange={(e) => handleChange('reference', e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Memo</label>
                  <textarea
                    placeholder="Additional notes about this transaction"
                    value={formData?.memo}
                    onChange={(e) => handleChange('memo', e?.target?.value)}
                    className="w-full p-3 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}

          {formData?.amount && formData?.description && (
            <div className="bg-muted/20 border border-border rounded-lg p-4">
              <h4 className="text-sm font-medium text-foreground mb-2">Transaction Preview</h4>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {showCustomerFields && formData?.customerName 
                      ? `${formData?.category === 'customer_payment' ? 'Payment from' : 'Advance from'} ${formData?.customerName}` 
                      : showVendorFields && formData?.vendorName
                      ? `${formData?.category === 'vendor_payment' ? 'Payment to' : 'Advance to'} ${formData?.vendorName}`
                      : showTransferFields && formData?.transferToBankName
                      ? `Transfer to ${formData?.transferToBankName}`
                      : formData?.description}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {getSelectedCategoryLabel()} • {formData?.date}
                  </p>
                  {(showCustomerFields || showVendorFields || showTransferFields || isAccountBasedCategory) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData?.description}
                    </p>
                  )}
                  {formData?.selectedInvoice && (
                    <p className="text-xs text-success mt-1">
                      📄 Linked to: {customerInvoices?.find(inv => inv?.id === formData?.selectedInvoice)?.invoice_number}
                    </p>
                  )}
                  {formData?.selectedBill && (
                    <p className="text-xs text-success mt-1">
                      📄 Linked to: {vendorBills?.find(bill => bill?.id === formData?.selectedBill)?.bill_number}
                    </p>
                  )}
                  {isAccountBasedCategory && (
                    <p className="text-xs text-primary mt-1">
                      🏦 Account Category: {getSelectedCategoryLabel()}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className={`text-sm font-semibold ${
                    formData?.transactionType === 'income' ? 'text-success' : 'text-error'
                  }`}>
                    {formData?.transactionType === 'income' ? '+' : '-'}
                    ${Math.abs(parseFloat(formData?.amount) || 0)?.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Plus"
              iconPosition="left"
            >
              Add Transaction
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualTransactionForm;